import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { FormStepId } from 'shared/config/formStepId';
import { useAppDispatch } from 'shared/lib';
import type { IInitialEvent } from 'shared/lib/sendGAEvents';

import type { TCreditFormCommonFields } from 'entities/creditForm';

import type { TValidateUserDataThunkPayload, TStepValidationErrors } from './types';
import { validateErrorHandlerThunk } from './validateErrorHandlerThunk';
import { validateSuccessHandlerThunk } from './validateSuccessHandlerThunk';
import { validateUserDataThunk } from './validateUserDataThunk';

type TValidateDataOptions = Omit<TValidateUserDataThunkPayload, 'onSuccess' | 'onError'> & {
  analyticsOverrides?: Partial<Pick<IInitialEvent, 'event' | 'eventCategory'>>;
  onSuccess?: (
    formFields: TCreditFormCommonFields,
    lastValidatedStepId: ValuesOf<typeof FormStepId>,
  ) => void | Promise<void>;
  onError?: (firstStepWithError: ValuesOf<typeof FormStepId>, errors: TStepValidationErrors[]) => void;
  stepsToValidate: Array<ValuesOf<typeof FormStepId>>;
};

export const useValidateData = (options: TValidateDataOptions, isEnabledValidationOnMount = true) => {
  const dispatch = useAppDispatch();
  const optionsRef = useRef(options);

  const validate = useCallback(() => {
    const {
      onSuccess: onSuccessCallback,
      onError: onErrorCallback,
      onStepSuccess,
      analyticsOverrides,
      onComplete,
      stepsToValidate,
    } = optionsRef.current;

    const onError: TValidateUserDataThunkPayload['onError'] = (
      errors: TStepValidationErrors[],
      validatedSteps: Array<ValuesOf<typeof FormStepId>>,
    ) => {
      dispatch(validateErrorHandlerThunk(errors, validatedSteps, analyticsOverrides));
      onErrorCallback?.(errors[0].formStepId, errors);
    };

    const onSuccess = (
      formFields: TCreditFormCommonFields,
      validatedSteps: Array<ValuesOf<typeof FormStepId>>,
      lastValidatedStepId: ValuesOf<typeof FormStepId>,
    ) => {
      dispatch(validateSuccessHandlerThunk(validatedSteps, analyticsOverrides));
      onSuccessCallback?.(formFields, lastValidatedStepId);
    };

    dispatch(
      validateUserDataThunk({
        onComplete,
        onSuccess,
        onError,
        stepsToValidate,
        onStepSuccess,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  useEffect(() => {
    if (isEnabledValidationOnMount) {
      validate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(() => ({ validate }), [validate]);
};
